(ns shared.core.data
  (:require [shared.core.config :as config]
            [shared.core.reactive :as re]
            [shared.core.toasts.data]
            [shared.core.util :as util]
            [goog.dom :as dom]
            [goog.events :as gevents]
            [clojure.string :as s]))

(def root-path (util/ns->keyword-path ::root))
(def nav-path (conj root-path :nav))
(def viewport-size-path (conj root-path :viewport-size))
(def online-path (conj root-path :online))
(def country-code-path (conj root-path :country-code))

(re/reg-sub
  :core/initialized?-
  (fn [db]
    (:initialized? db)))

(re/reg-event-fx
  :core/initialize re/middleware
  (fn [world]
    {:db {:initialized? true} :local-storage (:local-storage world)}))

(re/reg-sub
  :core/nav-expanded-ids-
  (fn [db]
    (get-in (:local-storage db) (conj nav-path :expanded-ids))))

(re/reg-sub
  :core/nav-id-expanded?-
  (fn [db [_ id]]
    (contains? (get-in (:local-storage db) (conj nav-path :expanded-ids) #{}) id)))

(re/reg-event-fx
  :core/toggle-nav re/middleware
  (fn [{:keys [local-storage]} [id]]
    (let [p (conj nav-path :expanded-ids)
          cur-ids (get-in local-storage p #{})]
      {:local-storage (assoc-in local-storage p (if (contains? cur-ids id) (disj cur-ids id) (conj cur-ids id)))})))

(defn disabled-features [db]
  (get db :disabled-features #{}))

(re/reg-sub
  :core/feature-disabled?-
  (fn [db [_ feature]]
    (contains? (disabled-features db) feature)))

(re/reg-event-db
  :core/enable-feature re/middleware
  (fn [db [feature]]
    (assoc db :disabled-features (disj (disabled-features db) feature))))

(re/reg-event-db
  :core/disable-feature re/middleware
  (fn [db [feature]]
    (assoc db :disabled-features (conj (disabled-features db) feature))))

(re/reg-event-db
  :core/copy-to-clipboard re/middleware
  (fn [db [v {:keys [notify? notify-text]}]]
    (-> js/navigator
        (.-clipboard)
        (.writeText v))
    (when notify?
      (re/dispatch [:toasts/add-info {:msg (or notify-text (str "Successfully copied \"" v "\""))
                                      :duration 5}]))
    db))

(defn window-resize [db]
  (let [vs (dom/getViewportSize)]
    (assoc-in db viewport-size-path {:width (.-width vs)
                                     :height (.-height vs)})))

(re/reg-event-db
  :core/window-resize re/quiet-middleware
  window-resize)

(re/reg-event-db
  :core/scroll-to-by-element-id re/middleware
  (fn [db [element-id args]]
    (when-let [el (js/document.getElementById element-id)]
      (.scrollIntoView el (some-> args (util/->camelCaseString) (clj->js))))
    db))

(defn viewport-size [db]
  (or (get-in db viewport-size-path)
      (window-resize db)))

(re/reg-sub :core/viewport-size viewport-size)

(defn viewport-height [db]
  (or (:height (viewport-size db)) (:height (viewport-size (window-resize db)))))

(re/reg-sub :core/viewport-height viewport-height)

(defn viewport-width [db]
  (or (:width (viewport-size db)) (:width (viewport-size (window-resize db)))))

(re/reg-sub :core/viewport-width viewport-width)

(defn store-online [db]
  (assoc-in db online-path (.. js/window -navigator -onLine)))

(defn online? [db]
  (get-in db online-path))

(re/reg-sub :core/online?- online?)

(re/reg-event-db
  :core/store-online re/middleware
  store-online)

(re/reg-event-db
  :core/bind-window-events re/quiet-middleware
  (fn [db]
    (let [w (dom/getWindow)]
      (gevents/listen w goog.events.EventType.RESIZE #(re/dispatch [:core/window-resize]))
      (re/dispatch [:core/window-resize])
      (.addEventListener w "online" #(re/dispatch [:core/store-online]))
      (.addEventListener w "offline" #(re/dispatch [:core/store-online]))
      db)))

(re/reg-event-db
  :core/set-window-defaults re/quiet-middleware
  (fn [db]
    (-> db
        (window-resize)
        (store-online))))

(defn layout-content-container-scroll-position [db]
  (get-in db (conj root-path :layout-content-container-scroll-position)))

(re/reg-sub :core/layout-content-container-scroll-position- layout-content-container-scroll-position)

(re/reg-event-db
  :core/set-layout-content-container-scroll-position re/quiet-middleware
  (fn [db [pos]]
    (assoc-in db (conj root-path :layout-content-container-scroll-position) pos)))

(defn add-js-to-page [js-file]
  (let [script-tag (.createElement js/document "script")]
    (set! (.-type script-tag) "text/javascript")
    (set! (.-src script-tag) js-file)
    (.appendChild (.-head js/document) script-tag)
    script-tag))

(re/reg-event-fx
  :core/clear-local-storage re/middleware
  (fn [_]
    {:local-storage nil}))

(re/reg-event-fx
  :core/download re/middleware
  (fn [_ [{:keys [filename headers rows]}]]
    (let [a (.createElement js/document "a")
          csv-line (fn [list] (->> list
                                   (map #(str \" (s/replace (str %) #"\"" "\"\"") \"))
                                   (s/join ",")))
          csv-rows (->> rows
                        (concat [headers])
                        (map csv-line)
                        (s/join "\n"))]

      (.setAttribute a "href" (str "data:text/plain;charset=utf-8,"
                                   (js/encodeURIComponent csv-rows)))
      (.setAttribute a "download" filename)

      (set! (.. a -style -display) "none")
      (-> js/document .-body (.appendChild a))

      (.click a)

      (-> js/document .-body (.removeChild a))

     {})))

(re/reg-event-db
  :core/reload-page re/middleware
  (fn [db]
    (.reload js/window.location)
    db))

(re/reg-event-db
  :core/on-visibility-change re/middleware
  (fn [db [{:keys [on-visible on-hidden]}]]
    (js/document.addEventListener
      "visibilitychange"
      (fn []
        (if (= (.-visibilityState js/document) "visible")
          (when on-visible
            (on-visible))
          (when on-hidden
            (on-hidden)))))
    db))

(re/reg-sub
  :core/country-code-
  (fn [db]
    (get-in db country-code-path)))

(re/reg-event-db
  :core/set-country-code re/middleware
  (fn [db [country-code]]
    (config/set-country-code country-code)
    (assoc-in db country-code-path country-code)))
